// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file BlockedUsersContext.jsx
 * @description Contexto global para gestionar usuarios bloqueados
 * @version 1.0.0
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';
import { STORAGE_KEYS } from '../utils/constants';

/** Carga inicial una vez por usuario (StrictMode / doble mount). */
const __blockedInitialLoadForUser = new Set();

const BlockedUsersContext = createContext();

export const useBlockedUsers = () => {
  const context = useContext(BlockedUsersContext);
  if (!context) {
    throw new Error('useBlockedUsers debe usarse dentro de BlockedUsersProvider');
  }
  return context;
};

export const BlockedUsersProvider = ({ children }) => {
  const { user } = useAuth();
  const [blockedList, setBlockedList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const lastLoadedUserIdRef = useRef(null);
  const loadCallCountRef = useRef(0);

  /**
   * Cargar lista de bloqueados desde el servidor
   */
  const blockedListRef = useRef([]);
  
  // Mantener ref sincronizado con estado
  useEffect(() => {
    blockedListRef.current = blockedList;
  }, [blockedList]);

  const loadBlockedUsers = useCallback(async (force = false) => {
    const userId = user?.id || user?.usuario_id;
    if (!userId) {
      console.log('🚫 [BLOCKED] loadBlockedUsers: No userId, skipping');
      return;
    }

    // Evitar llamadas duplicadas
    if (isLoadingRef.current && !force) {
      console.log('⏸️ [BLOCKED] loadBlockedUsers: Ya hay una llamada en progreso, skipping');
      return;
    }

    const currentList = blockedListRef.current;
    if (!force && __blockedInitialLoadForUser.has(userId)) {
      console.log('[BLOCKED_FLOW][SKIP_DUPLICATE_LOAD]');
      return;
    }

    loadCallCountRef.current += 1;
    const callNumber = loadCallCountRef.current;

    console.log(`🔄 [BLOCKED] loadBlockedUsers llamado #${callNumber} para userId:`, userId, {
      force,
      estado_actual: currentList.length,
      timestamp: new Date().toISOString()
    });

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const response = await apiService.getBlockedUsers(userId);
      const blockedData = response?.data || [];

      console.log(`✅ [BLOCKED] getBlockedUsers respuesta #${callNumber}:`, {
        data: blockedData,
        length: blockedData.length,
        ids: blockedData.map(b => b.id)
      });

      // Solo actualizar si realmente cambió
      const currentIds = currentList.map(b => b.id).sort().join(',');
      const newIds = blockedData.map(b => b.id).sort().join(',');

      if (currentIds !== newIds || force) {
        console.log(`📋 [BLOCKED] Estado cambiado #${callNumber}:`, {
          anterior: currentList.length,
          nuevo: blockedData.length,
          anterior_ids: currentList.map(b => b.id),
          nuevo_ids: blockedData.map(b => b.id)
        });

        setBlockedList(blockedData);
        blockedListRef.current = blockedData;
        lastLoadedUserIdRef.current = userId;
        __blockedInitialLoadForUser.add(userId);
        localStorage.setItem(STORAGE_KEYS.BLOCKED, JSON.stringify(blockedData.map((b) => b.id)));

        console.log(`✅ [BLOCKED] Estado actualizado #${callNumber}: Array(${blockedData.length})`);
      } else {
        console.log(`⏸️ [BLOCKED] Estado sin cambios #${callNumber}, no actualizando`);
      }
    } catch (e) {
      console.error(`❌ [BLOCKED] Error cargando bloqueados #${callNumber}:`, e);
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.BLOCKED);
        if (stored) {
          const parsed = JSON.parse(stored);
          const blockedFromStorage = Array.isArray(parsed) ? parsed.map((id) => ({ id })) : [];
          console.log(`📋 [BLOCKED] Usando lista desde localStorage #${callNumber}:`, blockedFromStorage);
          setBlockedList(blockedFromStorage);
          blockedListRef.current = blockedFromStorage;
        }
      } catch (err) {
        console.error(`❌ [BLOCKED] Error parseando localStorage #${callNumber}:`, err);
      }
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [user?.id, user?.usuario_id]);

  /**
   * Toggle bloqueo de un usuario
   * CRÍTICO: NO recarga después del toggle, confía en el estado optimista
   * @param {number} targetId - ID del usuario a bloquear/desbloquear
   * @param {boolean} block - true para bloquear, false para desbloquear
   * @param {object} userData - Datos opcionales del usuario (alias, nombre, etc.)
   */
  const toggleBlock = useCallback(async (targetId, block = true, userData = null) => {
    const userId = user?.id || user?.usuario_id;
    const targetNum = Number(targetId);
    if (!userId || !Number.isFinite(targetNum) || targetNum <= 0) {
      console.error('🚫 [BLOCKED] toggleBlock: userId o targetId faltante', { userId, targetId });
      return { success: false, error: 'Usuario no identificado' };
    }

    const currentList = blockedListRef.current;
    const estadoPreToggle = [...currentList];
    const isBlocked = currentList.some((u) => Number(u.id) === targetNum);

    console.log('🔄 [BLOCKED] toggleBlock llamado:', {
      userId,
      targetId: targetNum,
      block,
      userData,
      estado_pre_toggle: estadoPreToggle.length,
      ids_pre_toggle: estadoPreToggle.map(u => u.id),
      isBlocked,
      accion: block ? 'BLOQUEAR' : 'DESBLOQUEAR'
    });

    // Actualización optimista
    let updatedList;
    if (block) {
      // Bloquear: agregar a la lista usando los datos del usuario si están disponibles
      const newUser = userData
        ? {
            id: targetNum,
            alias: userData.alias || null,
            nombre_real: userData.nombre_real || null,
            nombre: userData.nombre || null,
            username: userData.username || null,
            email: userData.email ?? null,
            avatar: userData.avatar ?? null,
            idioma_preferido: userData.idioma_preferido || null,
            pais_nombre: userData.pais_nombre ?? userData.pais ?? null,
            codigo_pais: userData.codigo_pais ?? userData.country_iso ?? null,
            country_iso: userData.country_iso ?? userData.codigo_pais ?? null,
            zona_horaria: userData.zona_horaria ?? null,
          }
        : {
            id: targetNum,
            nombre: null,
            nombre_real: null,
            username: null,
            alias: null,
            email: null,
            avatar: null,
            idioma_preferido: null,
            pais_nombre: null,
            codigo_pais: null,
            country_iso: null,
            zona_horaria: null,
          };
      const withoutDup = currentList.filter((u) => Number(u.id) !== targetNum);
      updatedList = [...withoutDup, newUser];
    } else {
      // Desbloquear: remover de la lista
      updatedList = currentList.filter((u) => Number(u.id) !== targetNum);
    }

    setBlockedList(updatedList);
    blockedListRef.current = updatedList;

    console.log('📋 [BLOCKED] Estado post-toggle optimista:', {
      length: updatedList.length,
      ids: updatedList.map(u => u.id),
      accion: block ? 'BLOQUEADO' : 'DESBLOQUEADO'
    });

    try {
      // Llamar a la API con el estado correcto
      const result = await apiService.toggleBlock(userId, targetNum, block);
      console.log('✅ [BLOCKED] toggleBlock respuesta del servidor:', result);

      // CRÍTICO: NO recargar después del toggle
      // El estado optimista es la fuente de verdad
      // Solo recargar si la API falla

      console.log('✅ [BLOCKED] Toggle aplicado exitosamente:', {
        targetId: targetNum,
        accion: block ? 'BLOQUEADO' : 'DESBLOQUEADO',
        estado_final: updatedList.length
      });

      return { success: true, data: result };
    } catch (e) {
      console.error('❌ [BLOCKED] Error en toggleBlock:', e);
      // Revertir actualización optimista en caso de error
      setBlockedList(estadoPreToggle);
      blockedListRef.current = estadoPreToggle;
      return { success: false, error: e.message || 'No se pudo bloquear/desbloquear' };
    }
  }, [user?.id, user?.usuario_id]);

  /**
   * Verificar si un usuario está bloqueado
   */
  const isBlocked = useCallback((userId) => {
    const n = Number(userId);
    if (!Number.isFinite(n) || n <= 0) return false;
    return blockedList.some((u) => Number(u.id) === n);
  }, [blockedList]);

  /**
   * Carga inicial SOLO cuando cambia el usuario
   * CRÍTICO: No recargar en cada render
   */
  useEffect(() => {
    const userId = user?.id || user?.usuario_id;
    if (!userId) {
      __blockedInitialLoadForUser.clear();
      return;
    }
    if (__blockedInitialLoadForUser.has(userId)) {
      console.log('[BLOCKED_FLOW][SKIP_DUPLICATE_LOAD]');
      return;
    }
    console.log('🔄 [BLOCKED] Carga inicial de bloqueados para userId:', userId);
    loadBlockedUsers(false);
    // NO incluir loadBlockedUsers en dependencias para evitar loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.usuario_id]);

  const value = {
    blockedList,
    isLoading,
    loadBlockedUsers,
    toggleBlock,
    isBlocked
  };

  return (
    <BlockedUsersContext.Provider value={value}>
      {children}
    </BlockedUsersContext.Provider>
  );
};
